import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Typography, Container, Grid, Avatar, TextField, Paper, Chip } from "@mui/material";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SecurityIcon from "@mui/icons-material/Security";
import BarChartIcon from "@mui/icons-material/BarChart";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ChatIcon from "@mui/icons-material/Chat";
import StorageIcon from "@mui/icons-material/Storage";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ForumIcon from "@mui/icons-material/Forum";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import PeopleIcon from "@mui/icons-material/People";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GradeIcon from "@mui/icons-material/Grade";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import logoImg from "../assets/images/talktokam.webp";
import { BRAND } from "../theme/talktokam";
import Header from "../components/CommonAppLayout/Header";
import Footer from "../components/CommonAppLayout/Footer";

const TiltCard = ({ children, max = 12, scale = 1.02, style = {}, ...rest }) => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 220, damping: 18 });
  const sry = useSpring(ry, { stiffness: 220, damping: 18 });

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    ry.set((px - 0.5) * 2 * max);
    rx.set(-(py - 0.5) * 2 * max);
  };
  const onLeave = () => { rx.set(0); ry.set(0); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: srx, rotateY: sry, scale, transformPerspective: 1200, transformStyle: "preserve-3d", ...style }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

const MockAnalyticsDashboard = () => {
  const kpis = [
    { label: "Total Conversations", value: "1,247", sub: "Last 7 days", icon: <ForumIcon sx={{ fontSize: 22 }} />, grad: "linear-gradient(135deg, #6366f1, #8b5cf6)", light: "#eef2ff" },
    { label: "Total Messages", value: "8,432", sub: "6.8 avg per chat", icon: <AutoGraphIcon sx={{ fontSize: 22 }} />, grad: "linear-gradient(135deg, #f59e0b, #f97316)", light: "#fffbeb" },
    { label: "Active Chats", value: "63", sub: "82% closure rate", icon: <PeopleIcon sx={{ fontSize: 22 }} />, grad: "linear-gradient(135deg, #10b981, #34d399)", light: "#ecfdf5" },
    { label: "Top User", value: "Aman", sub: "342 messages", icon: <EmojiEventsIcon sx={{ fontSize: 22 }} />, grad: "linear-gradient(135deg, #ec4899, #f472b6)", light: "#fdf2f8" },
    { label: "Avg Rating", value: "4.8 ⭐", sub: "126 ratings", icon: <GradeIcon sx={{ fontSize: 22 }} />, grad: "linear-gradient(135deg, #f59e0b, #fbbf24)", light: "#fef3c7" },
  ];
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const vals = [120, 180, 150, 220, 195, 280, 240];
  const maxV = Math.max(...vals);
  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflow: "hidden", bgcolor: "rgba(255,255,255,0.97)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.3)", boxShadow: "0 30px 80px rgba(0,0,0,0.4)", willChange: "transform" }}>
      <Box sx={{ p: 2.5, borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: BRAND.primary, animation: "pulse 2s infinite" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Live Status</Typography>
        </Box>
        {[{ l: "Active", c: "#10b981", v: 63 }, { l: "Live", c: "#f59e0b", v: 18 }, { l: "Closed", c: "#6b7280", v: 1023 }].map(s => (
          <Box key={s.l} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.c }} />
            <Typography sx={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>{s.l}: {s.v}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1.5 }}>
        {kpis.map((k, i) => (
          <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: k.light, border: "1px solid #f3f4f6" }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: k.grad, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", mb: 1, boxShadow: "0 4px 12px rgba(99,102,241,0.2)" }}>{k.icon}</Box>
            <Typography sx={{ fontSize: 10, color: "#6b7280", fontWeight: 600, mb: 0.3 }}>{k.label}</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827", lineHeight: 1.1, mb: 0.3 }}>{k.value}</Typography>
            <Typography sx={{ fontSize: 9, color: "#9ca3af" }}>{k.sub}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ p: 2.5, borderTop: "1px solid #f3f4f6" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>Conversations — Last 7 days</Typography>
          <Chip label="1,247 total" size="small" sx={{ bgcolor: "#eef2ff", color: BRAND.primary, fontWeight: 700, fontSize: 10, height: 22 }} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5, height: 130, pt: 3 }}>
          {vals.map((v, i) => (
            <Box key={i} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, height: "100%", justifyContent: "flex-end" }}>
              <Box sx={{ width: "100%", height: `${(v / maxV) * 100}%`, minHeight: 6, borderRadius: 1.5, background: "linear-gradient(180deg, #6366f1, #8b5cf6)", boxShadow: "0 4px 12px rgba(99,102,241,0.3)", position: "relative" }}>
                <Box sx={{ position: "absolute", top: -18, left: "50%", transform: "translateX(-50%)", fontSize: 9, fontWeight: 800, color: "#111827", bgcolor: "rgba(255,255,255,0.95)", px: 0.6, py: 0.1, borderRadius: 0.6, lineHeight: 1.2, whiteSpace: "nowrap" }}>{v}</Box>
              </Box>
              <Typography sx={{ fontSize: 10, color: "#6b7280", fontWeight: 600 }}>{days[i]}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

const MockFlowBuilder = () => {
  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflow: "hidden", bgcolor: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 30px 80px rgba(0,0,0,0.4)", display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.15fr 1fr" }, minHeight: { xs: 580, md: 620 } }}>
      <Box sx={{ display: "flex", flexDirection: "column", borderRight: { md: "1px solid #e5e7eb" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, borderBottom: "1px solid #e5e7eb", bgcolor: "#fff", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: 1.5, background: "linear-gradient(135deg, #6366f1, #a855f7)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(99,102,241,0.3)" }}>
              <AccountTreeIcon sx={{ fontSize: 16, color: "#fff" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>Welcome Flow</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.2 }}>
                <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#10b981", boxShadow: "0 0 4px #10b981" }} />
                <Typography sx={{ fontSize: 9, color: "#10b981", fontWeight: 700 }}>Auto-saved 2s ago</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexShrink: 0 }}>
            <Box sx={{ px: 1.2, py: 0.6, borderRadius: 1, border: "1px solid #e5e7eb", bgcolor: "#fff", fontSize: 10, color: "#374151", fontWeight: 700 }}>Test</Box>
            <Box sx={{ px: 1.4, py: 0.6, borderRadius: 1, background: "linear-gradient(135deg, #6366f1, #a855f7)", color: "#fff", fontSize: 10, fontWeight: 800, boxShadow: "0 4px 12px rgba(99,102,241,0.4)" }}>Publish</Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 2, py: 1, borderBottom: "1px solid #e5e7eb", bgcolor: "#fafbff" }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1, py: 0.3, borderRadius: 0.8, bgcolor: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.3)" }}>
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#a855f7", boxShadow: "0 0 6px #a855f7" }} />
            <Typography sx={{ fontSize: 9, color: "#a855f7", fontWeight: 800, letterSpacing: "0.5px" }}>STEP 2 OF 4</Typography>
          </Box>
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: "#e5e7eb", overflow: "hidden" }}>
            <Box sx={{ width: "50%", height: "100%", background: "linear-gradient(90deg, #38bdf8, #a855f7, #6366f1)" }} />
          </Box>
          <Typography sx={{ fontSize: 9, color: "#6b7280", fontWeight: 700, flexShrink: 0 }}>50%</Typography>
        </Box>
        <Box sx={{ position: "relative", flex: 1, overflow: "hidden", background: "radial-gradient(circle, #e5e7eb 1px, transparent 1px)", backgroundSize: "16px 16px", backgroundPosition: "0 0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 2 }}>
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
            <defs>
              <marker id="fArrow" markerWidth="10" markerHeight="10" refX="5" refY="9" orient="auto">
                <path d="M0,0 L0,10 L10,9 z" fill="#6366f1" />
              </marker>
              <linearGradient id="fLine" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#6366f1" />
              </linearGradient>
              <linearGradient id="fLineActive" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#6366f1" />
              </linearGradient>
            </defs>
            <line x1="50%" y1="55" x2="50%" y2="105" stroke="url(#fLineActive)" strokeWidth="3" markerEnd="url(#fArrow)" className="flow-line" />
            <line x1="50%" y1="155" x2="50%" y2="205" stroke="url(#fLine)" strokeWidth="3" markerEnd="url(#fArrow)" className="flow-line" />
            <line x1="50%" y1="255" x2="50%" y2="305" stroke="url(#fLine)" strokeWidth="3" markerEnd="url(#fArrow)" className="flow-line" />
            <g transform="translate(0, 120)">
              <rect x="50%" y="-9" width="52" height="18" rx="9" transform="translate(-26, 0)" fill="#0a0e27" stroke="#a855f7" strokeWidth="1" />
              <text x="50%" y="4" textAnchor="middle" fontSize="9" fontWeight="800" fill="#c4b5fd" fontFamily="monospace">→ name</text>
            </g>
            <g transform="translate(0, 220)">
              <rect x="50%" y="-9" width="52" height="18" rx="9" transform="translate(-26, 0)" fill="#0a0e27" stroke="#06b6d4" strokeWidth="1" />
              <text x="50%" y="4" textAnchor="middle" fontSize="9" fontWeight="800" fill="#67e8f9" fontFamily="monospace">→ email</text>
            </g>
            <g transform="translate(0, 320)">
              <rect x="50%" y="-9" width="52" height="18" rx="9" transform="translate(-26, 0)" fill="#0a0e27" stroke="#10b981" strokeWidth="1" />
              <text x="50%" y="4" textAnchor="middle" fontSize="9" fontWeight="800" fill="#6ee7b7" fontFamily="monospace">→ done</text>
            </g>
          </svg>
          <Box sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
            <Box sx={{ position: "relative", width: 220, p: 1.2, borderRadius: 2, bgcolor: "#fff", border: "2px solid #06b6d4", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
              <Box sx={{ position: "absolute", top: -10, left: -10, width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg, #06b6d4, #0891b2)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, boxShadow: "0 4px 12px rgba(6,182,212,0.5)" }}>1</Box>
              <Box sx={{ position: "absolute", top: -8, right: -8, width: 16, height: 16, borderRadius: "50%", background: "#10b981", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, border: "2px solid #fff" }}>✓</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981" }} />
                <Typography sx={{ fontSize: 9, color: "#10b981", fontWeight: 800, letterSpacing: "0.5px" }}>START</Typography>
                <Box sx={{ ml: "auto", px: 0.5, py: 0.1, borderRadius: 0.4, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                  <Typography sx={{ fontSize: 7, color: "#059669", fontWeight: 800 }}>~3s</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>Welcome!</Typography>
            </Box>
            <Box sx={{ height: 25, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ px: 0.8, py: 0.2, borderRadius: 1, bgcolor: "#0a0e27", border: "1px solid rgba(168,85,247,0.4)", color: "#c4b5fd", fontSize: 8, fontWeight: 800, fontFamily: "monospace" }}>+ ADD NODE</Box>
            </Box>
            <Box sx={{ position: "relative", width: 220, p: 1.2, borderRadius: 2, bgcolor: "#fff", border: "2px solid #a855f7", boxShadow: "0 0 0 4px rgba(168,85,247,0.15), 0 8px 24px rgba(168,85,247,0.35)", transform: "scale(1.04)" }}>
              <Box sx={{ position: "absolute", top: -10, left: -10, width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg, #a855f7, #7c3aed)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, boxShadow: "0 4px 12px rgba(168,85,247,0.5)" }}>2</Box>
              <Box sx={{ position: "absolute", top: -28, right: 0, px: 0.8, py: 0.2, borderRadius: 0.6, background: "linear-gradient(135deg, #a855f7, #6366f1)", color: "#fff", fontSize: 8, fontWeight: 800, letterSpacing: "0.5px" }}>● ACTIVE</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#a855f7" }} />
                <Typography sx={{ fontSize: 9, color: "#a855f7", fontWeight: 800, letterSpacing: "0.5px" }}>QUESTION</Typography>
                <Box sx={{ ml: "auto", px: 0.5, py: 0.1, borderRadius: 0.4, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                  <Typography sx={{ fontSize: 7, color: "#059669", fontWeight: 800 }}>~8s</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#111827", lineHeight: 1.2, mb: 0.4 }}>What's your name?</Typography>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, px: 0.6, py: 0.15, borderRadius: 0.5, bgcolor: "#0a0e27" }}>
                <Typography sx={{ fontSize: 8, color: "#94a3b8", fontFamily: "monospace" }}>→ name</Typography>
              </Box>
            </Box>
            <Box sx={{ height: 25, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ px: 0.8, py: 0.2, borderRadius: 1, bgcolor: "#0a0e27", border: "1px solid rgba(56,189,248,0.4)", color: "#67e8f9", fontSize: 8, fontWeight: 800, fontFamily: "monospace" }}>+ ADD NODE</Box>
            </Box>
            <Box sx={{ position: "relative", width: 220, p: 1.2, borderRadius: 2, bgcolor: "#fff", border: "2px solid #6366f1", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
              <Box sx={{ position: "absolute", top: -10, left: -10, width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, boxShadow: "0 4px 12px rgba(99,102,241,0.5)" }}>3</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: BRAND.cyan }} />
                <Typography sx={{ fontSize: 9, color: BRAND.cyan, fontWeight: 800, letterSpacing: "0.5px" }}>EMAIL</Typography>
                <Box sx={{ ml: "auto", px: 0.5, py: 0.1, borderRadius: 0.4, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                  <Typography sx={{ fontSize: 7, color: "#059669", fontWeight: 800 }}>~5s</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>Your email?</Typography>
            </Box>
            <Box sx={{ height: 25, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ px: 0.8, py: 0.2, borderRadius: 1, bgcolor: "#0a0e27", border: "1px solid rgba(16,185,129,0.4)", color: "#6ee7b7", fontSize: 8, fontWeight: 800, fontFamily: "monospace" }}>+ ADD NODE</Box>
            </Box>
            <Box sx={{ position: "relative", width: 220, p: 1.2, borderRadius: 2, bgcolor: "#fff", border: "2px solid #10b981", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
              <Box sx={{ position: "absolute", top: -10, left: -10, width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, boxShadow: "0 4px 12px rgba(16,185,129,0.5)" }}>4</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981" }} />
                <Typography sx={{ fontSize: 9, color: "#10b981", fontWeight: 800, letterSpacing: "0.5px" }}>END</Typography>
                <Box sx={{ ml: "auto", px: 0.5, py: 0.1, borderRadius: 0.4, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                  <Typography sx={{ fontSize: 7, color: "#059669", fontWeight: 800 }}>~2s</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#111827", lineHeight: 1.2, mt: 0.2 }}>Thank you!</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", background: "linear-gradient(135deg, #0a0e27 0%, #1e1b4b 50%, #0a0e27 100%)", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", top: -50, right: -50, width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.4), transparent 70%)", filter: "blur(50px)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -50, left: -50, width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(56,189,248,0.3), transparent 70%)", filter: "blur(50px)", pointerEvents: "none" }} />
        <Box sx={{ position: "relative", p: 2, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box sx={{ position: "relative", width: 8, height: 8 }}>
              <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", bgcolor: "#10b981" }} />
              <Box sx={{ position: "absolute", inset: -4, borderRadius: "50%", bgcolor: "#10b981", opacity: 0.4, animation: "talktokamPulse 1.5s infinite" }} />
            </Box>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#a5f3fc", letterSpacing: 1.2 }}>LIVE PREVIEW</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: 0.8, bgcolor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
            <SmartToyIcon sx={{ fontSize: 10, color: "#c4b5fd" }} />
            <Typography sx={{ fontSize: 9, color: "#c4b5fd", fontWeight: 700 }}>Welcome Flow</Typography>
          </Box>
        </Box>
        <Box sx={{ position: "relative", flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 1 }}>
          <Box sx={{ position: "relative", width: { xs: 220, md: 250 }, height: { xs: 440, md: 500 }, borderRadius: 5, background: "linear-gradient(145deg, #1f2937, #111827)", p: 1.2, boxShadow: "0 40px 80px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.1)" }}>
            <Box sx={{ position: "absolute", left: -2, top: 80, width: 2, height: 24, bgcolor: "#374151", borderRadius: "2px 0 0 2px" }} />
            <Box sx={{ position: "absolute", left: -2, top: 120, width: 2, height: 40, bgcolor: "#374151", borderRadius: "2px 0 0 2px" }} />
            <Box sx={{ position: "absolute", right: -2, top: 100, width: 2, height: 60, bgcolor: "#374151", borderRadius: "0 2px 2px 0" }} />
            <Box sx={{ width: "100%", height: "100%", borderRadius: 3.5, bgcolor: "#f9fafb", overflow: "hidden", display: "flex", flexDirection: "column", position: "relative" }}>
              <Box sx={{ position: "absolute", top: 6, left: "50%", transform: "translateX(-50%)", width: 70, height: 16, borderRadius: 8, bgcolor: "#000", zIndex: 10 }} />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, pt: 1.2, pb: 0.5, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 9, fontWeight: 800, color: "#111827" }}>9:41</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                  <Box sx={{ width: 8, height: 5, borderRadius: 0.5, bgcolor: "#111827" }} />
                  <Box sx={{ width: 6, height: 5, borderRadius: 0.5, border: "1px solid #111827" }} />
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.2, py: 1, background: "linear-gradient(135deg, #6366f1, #a855f7)", flexShrink: 0, mt: 0.5 }}>
                <Box sx={{ position: "relative" }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: "rgba(255,255,255,0.2)", border: "1.5px solid rgba(255,255,255,0.4)" }}>
                    <SmartToyIcon sx={{ fontSize: 16, color: "#fff" }} />
                  </Avatar>
                  <Box sx={{ position: "absolute", bottom: -1, right: -1, width: 9, height: 9, borderRadius: "50%", bgcolor: "#10b981", border: "1.5px solid #fff" }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, color: "#fff", fontWeight: 800, lineHeight: 1.1 }}>Support Bot</Typography>
                  <Typography sx={{ fontSize: 8, color: "#e0e7ff", fontWeight: 600 }}>Online · replies instantly</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.3, alignItems: "center" }}>
                  <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.7)" }} />
                  <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.7)" }} />
                  <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.7)" }} />
                </Box>
              </Box>
              <Box sx={{ flex: 1, p: 1.2, display: "flex", flexDirection: "column", gap: 0.7, overflow: "hidden", bgcolor: "#f9fafb" }}>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end", maxWidth: "88%" }}>
                  <Avatar sx={{ width: 20, height: 20, bgcolor: "#ede9fe", mb: 0.3, flexShrink: 0 }}>
                    <SmartToyIcon sx={{ fontSize: 11, color: "#7c3aed" }} />
                  </Avatar>
                  <Box>
                    <Box sx={{ px: 1, py: 0.7, borderRadius: "12px 12px 12px 3px", bgcolor: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
                      <Typography sx={{ fontSize: 9.5, color: "#111827", lineHeight: 1.3, fontWeight: 500 }}>👋 Hi there! Welcome to Talktokam.</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 7, color: "#9ca3af", mt: 0.2, ml: 0.5, fontWeight: 600 }}>9:41 AM</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end", maxWidth: "88%" }}>
                  <Box sx={{ width: 20, flexShrink: 0 }} />
                  <Box>
                    <Box sx={{ px: 1, py: 0.7, borderRadius: "12px 12px 12px 3px", bgcolor: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
                      <Typography sx={{ fontSize: 9.5, color: "#111827", lineHeight: 1.3, fontWeight: 500 }}>What's your name?</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 7, color: "#9ca3af", mt: 0.2, ml: 0.5, fontWeight: 600 }}>9:41 AM</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Box sx={{ maxWidth: "70%" }}>
                    <Box sx={{ px: 1, py: 0.7, borderRadius: "12px 12px 3px 12px", background: "linear-gradient(135deg, #6366f1, #a855f7)", boxShadow: "0 4px 12px rgba(99,102,241,0.3)" }}>
                      <Typography sx={{ fontSize: 9.5, color: "#fff", lineHeight: 1.3, fontWeight: 600 }}>Aman</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 7, color: "#9ca3af", mt: 0.2, textAlign: "right", mr: 0.5, fontWeight: 600 }}>9:41 AM ✓✓</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end", maxWidth: "88%" }}>
                  <Avatar sx={{ width: 20, height: 20, bgcolor: "#ede9fe", mb: 0.3, flexShrink: 0 }}>
                    <SmartToyIcon sx={{ fontSize: 11, color: "#7c3aed" }} />
                  </Avatar>
                  <Box>
                    <Box sx={{ px: 1, py: 0.7, borderRadius: "12px 12px 12px 3px", bgcolor: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
                      <Typography sx={{ fontSize: 9.5, color: "#111827", lineHeight: 1.3, fontWeight: 500 }}>Nice to meet you, Aman! 😊</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 7, color: "#9ca3af", mt: 0.2, ml: 0.5, fontWeight: 600 }}>9:41 AM</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end", maxWidth: "88%" }}>
                  <Box sx={{ width: 20, flexShrink: 0 }} />
                  <Box>
                    <Box sx={{ px: 1, py: 0.7, borderRadius: "12px 12px 12px 3px", bgcolor: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
                      <Typography sx={{ fontSize: 9.5, color: "#111827", lineHeight: 1.3, fontWeight: 500 }}>What's your email address?</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 7, color: "#9ca3af", mt: 0.2, ml: 0.5, fontWeight: 600 }}>9:42 AM</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end", maxWidth: "88%" }}>
                  <Box sx={{ width: 20, flexShrink: 0 }} />
                  <Box sx={{ px: 1.2, py: 0.9, borderRadius: "12px 12px 12px 3px", bgcolor: "#fff", border: "1px solid #e5e7eb", display: "flex", gap: 0.4, alignItems: "center", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#a855f7", animation: "talktokamTyping 1.2s infinite" }} />
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#6366f1", animation: "talktokamTyping 1.2s infinite", animationDelay: "0.15s" }} />
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#38bdf8", animation: "talktokamTyping 1.2s infinite", animationDelay: "0.3s" }} />
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 1, borderTop: "1px solid #e5e7eb", display: "flex", gap: 0.5, alignItems: "center", bgcolor: "#fff", flexShrink: 0 }}>
                <Box sx={{ flex: 1, px: 1, py: 0.6, borderRadius: 2, bgcolor: "#f3f4f6", display: "flex", alignItems: "center", gap: 0.4 }}>
                  <Typography sx={{ fontSize: 8.5, color: "#9ca3af" }}>Type a message...</Typography>
                </Box>
                <Box sx={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #6366f1, #a855f7)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(99,102,241,0.4)" }}>
                  <SendIcon sx={{ fontSize: 13, color: "#fff" }} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ position: "relative", p: 1.5, pt: 0.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, px: 1, py: 0.3, borderRadius: 0.8, bgcolor: "rgba(168,85,247,0.15)", border: "1px solid rgba(168,85,247,0.3)" }}>
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#a855f7", boxShadow: "0 0 6px #a855f7" }} />
            <Typography sx={{ fontSize: 8, color: "#c4b5fd", fontWeight: 800, letterSpacing: "0.3px" }}>TESTING IN REAL-TIME</Typography>
          </Box>
          <Typography sx={{ fontSize: 8, color: "#94a3b8", fontWeight: 600 }}>Mobile · 9:41 AM</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const MockAIAgentCard = () => {
  const agents = [
    { name: "Sales Bot", msgs: 1247, color: "linear-gradient(135deg, #38bdf8, #0ea5e9)", desc: "Qualifies leads & books demos", active: true },
    { name: "Support AI", msgs: 3421, color: "linear-gradient(135deg, #a855f7, #9333ea)", desc: "24/7 customer support", active: true },
    { name: "FAQ Helper", msgs: 892, color: "linear-gradient(135deg, #6366f1, #8b5cf6)", desc: "Instant answers from KB", active: true },
    { name: "Lead Qualifier", msgs: 567, color: "linear-gradient(135deg, #ec4899, #f472b6)", desc: "Asks qualifying questions", active: false },
  ];
  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflow: "hidden", bgcolor: "#fff", boxShadow: "0 30px 80px rgba(0,0,0,0.4)", border: "1px solid #e5e7eb" }}>
      <Box sx={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", p: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SmartToyIcon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 16, color: "#fff" }}>Create AI Agent</Typography>
          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.75)" }}>Give your agent a name</Typography>
        </Box>
        <Button size="small" sx={{ ml: "auto", minWidth: 0, color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }, borderRadius: 1.5 }} startIcon={<AddIcon sx={{ fontSize: 14 }} />}>
          New
        </Button>
      </Box>
      <Box sx={{ p: 2.5, borderBottom: "1px solid #f3f4f6" }}>
        <Typography sx={{ fontSize: 10, color: "#6b7280", fontWeight: 600, mb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}>Agent Name</Typography>
        <TextField fullWidth size="small" value="Sales Bot" placeholder="e.g. Sales Bot, Support Agent..." sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }} />
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Button fullWidth size="small" variant="outlined" sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 700 }}>Cancel</Button>
          <Button fullWidth size="small" variant="contained" sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 700, background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }} startIcon={<span>✨</span>}>Create</Button>
        </Box>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: 10, color: "#6b7280", fontWeight: 700, mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>Your Agents</Typography>
        {agents.map((a, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.2, p: 1, mb: 0.5, borderRadius: 1.5, bgcolor: "#fafafa", border: "1px solid #f3f4f6" }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1, background: a.color, color: "#fff", fontWeight: 800, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>{a.name.charAt(0)}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>{a.name}</Typography>
              <Typography sx={{ fontSize: 10, color: "#6b7280" }} noWrap>{a.desc}</Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: 10, color: BRAND.primary, fontWeight: 700 }}>{a.msgs}</Typography>
              <Typography sx={{ fontSize: 8, color: "#9ca3af" }}>msgs</Typography>
            </Box>
            <Box sx={{ width: 32, height: 18, borderRadius: 9, bgcolor: a.active ? BRAND.cyan : "#e5e7eb", position: "relative" }}>
              <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: "#fff", position: "absolute", top: 2, left: a.active ? 16 : 2, transition: "0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const MockLiveChat = () => {
  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflow: "hidden", bgcolor: "#fff", boxShadow: "0 30px 80px rgba(0,0,0,0.4)", border: "1px solid #e5e7eb", maxWidth: 480, mx: "auto" }}>
      <Box sx={{ p: 2, borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 1.5, background: "linear-gradient(135deg, #fafbff, #fff)" }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SmartToyIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Support AI</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#10b981" }} />
            <Typography sx={{ fontSize: 10, color: "#10b981", fontWeight: 600 }}>Online · Avg reply 2s</Typography>
          </Box>
        </Box>
        <Chip label="Live" size="small" sx={{ bgcolor: "#ecfdf5", color: "#059669", fontWeight: 700, fontSize: 10, height: 22 }} />
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, minHeight: 320, background: "#fafbff" }}>
        <Box sx={{ alignSelf: "flex-start", maxWidth: "75%" }}>
          <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", fontSize: 12, color: "#111827", lineHeight: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
            👋 Hi there! I'm your AI assistant. How can I help you today?
          </Box>
          <Typography sx={{ fontSize: 9, color: "#9ca3af", mt: 0.3, ml: 0.5 }}>10:23 AM</Typography>
        </Box>
        <Box sx={{ alignSelf: "flex-end", maxWidth: "75%" }}>
          <Box sx={{ p: 1.2, borderRadius: 2, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff", fontSize: 12, lineHeight: 1.5, boxShadow: "0 4px 12px rgba(99,102,241,0.3)" }}>
            I need help with my subscription
          </Box>
          <Typography sx={{ fontSize: 9, color: "#9ca3af", mt: 0.3, textAlign: "right", mr: 0.5 }}>10:23 AM ✓✓</Typography>
        </Box>
        <Box sx={{ alignSelf: "flex-start", maxWidth: "80%" }}>
          <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", fontSize: 12, color: "#111827", lineHeight: 1.5 }}>
            Of course! I can help you with that. Which plan are you on?
          </Box>
          <Typography sx={{ fontSize: 9, color: "#9ca3af", mt: 0.3, ml: 0.5 }}>10:23 AM</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap", ml: 0.5 }}>
          {["Starter", "Pro", "Enterprise"].map((p, i) => (
            <Chip key={i} label={p} size="small" onClick={() => { }} sx={{ bgcolor: "#eef2ff", color: BRAND.primary, fontWeight: 700, fontSize: 11, height: 26, cursor: "pointer", "&:hover": { bgcolor: "#c7d2fe" } }} />
          ))}
        </Box>
        <Box sx={{ alignSelf: "flex-start", maxWidth: "80%" }}>
          <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "#fff", border: "1px solid #e5e7eb", fontSize: 12, color: "#111827", lineHeight: 1.5 }}>
            Great choice! Pro includes unlimited bots, advanced analytics, and priority support. 🎉
          </Box>
          <Typography sx={{ fontSize: 9, color: "#9ca3af", mt: 0.3, ml: 0.5 }}>10:24 AM</Typography>
        </Box>
        <Box sx={{ alignSelf: "flex-start", display: "flex", gap: 0.4, p: 1.2, borderRadius: 2, bgcolor: "#fff", border: "1px solid #e5e7eb" }}>
          {[0, 1, 2].map(i => (
            <Box key={i} sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: BRAND.primary, animation: `typing 1s infinite ${i * 0.15}s` }} />
          ))}
        </Box>
      </Box>
      <Box sx={{ p: 1.5, borderTop: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 1, bgcolor: "#fff" }}>
        <TextField fullWidth size="small" placeholder="Type a message..." sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3, fontSize: 12, bgcolor: "#f9fafb" } }} />
        <Button variant="contained" sx={{ minWidth: 0, p: 1, borderRadius: 2, background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}>
          <SendIcon sx={{ fontSize: 16, color: "#fff" }} />
        </Button>
      </Box>
    </Paper>
  );
};

const StickySection = ({ sectionRef, mockup, kicker, title, titleAccent, description, points, align = "right", progress }) => {
  const dir = align === "right" ? 1 : -1;
  const mockScale = useTransform(progress, [0, 0.5, 1], [0.85, 1, 1.15]);
  const mockRotateY = useTransform(progress, [0, 0.5, 1], [15 * dir, 0, -10 * dir]);
  const mockRotateX = useTransform(progress, [0, 0.5, 1], [5, 0, -3]);
  const textY = useTransform(progress, [0, 0.5, 1], [40, 0, -40]);
  const mockY = useTransform(progress, [0, 0.5, 1], [-30, 0, 30]);

  return (
    <Box ref={sectionRef} sx={{ minHeight: "100vh", position: "relative", py: { xs: 2, md: 3 } }}>
      <Box sx={{
        position: "sticky",
        top: 80,
        minHeight: "calc(100vh - 80px)",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        background: `radial-gradient(ellipse at ${align === "right" ? "80% 20%" : "20% 80%"}, ${BRAND.cyan}1f 0%, transparent 50%), radial-gradient(ellipse at ${align === "right" ? "20% 80%" : "80% 20%"}, ${BRAND.purple}1f 0%, transparent 50%), linear-gradient(180deg, #0a0e27 0%, #050816 100%)`,
        borderRadius: { xs: 0, md: 4 },
        mx: { xs: 0, md: 2 },
      }}>
        <Container maxWidth="lg" sx={{ position: "relative", py: { xs: 6, md: 8 } }}>
          <Box sx={{ position: "absolute", top: 24, left: 24, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 80, fontWeight: 900, color: "rgba(255,255,255,0.04)", lineHeight: 1, letterSpacing: "-4px" }}>{kicker.split(" / ")[0]}</Typography>
            <Box sx={{ width: 4, height: 60, background: `linear-gradient(180deg, ${BRAND.cyan}, ${BRAND.purple})`, borderRadius: 2 }} />
            <Box>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: BRAND.cyan, textTransform: "uppercase", letterSpacing: "2px" }}>FEATURE</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{kicker.split(" / ")[1]}</Typography>
            </Box>
          </Box>
          <Grid container spacing={6} alignItems="center" sx={{ pt: { xs: 8, md: 6 } }}>
            <Grid item xs={12} md={5} sx={{ order: { xs: 2, md: align === "right" ? 1 : 2 } }}>
              <motion.div style={{ y: textY }}>
                <Typography sx={{ fontSize: { xs: 36, md: 48, lg: 56 }, fontWeight: 800, color: "#fff", mb: 2, letterSpacing: "-1.5px", lineHeight: 1.05 }}>
                  {title}{" "}
                  <Box component="span" sx={{ background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    {titleAccent}
                  </Box>
                </Typography>
                <Typography sx={{ color: "#94a3b8", fontSize: 16, lineHeight: 1.7, mb: 3, maxWidth: 480 }}>{description}</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                  {points.map((p, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: 1, background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.3 }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />
                      </Box>
                      <Typography sx={{ color: "#cbd5e1", fontSize: 14, fontWeight: 500, lineHeight: 1.6 }}>{p}</Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: align === "right" ? 2 : 1 } }}>
              <motion.div style={{
                scale: mockScale,
                y: mockY,
                rotateY: mockRotateY,
                rotateX: mockRotateX,
                transformStyle: "preserve-3d",
                transformPerspective: 1500,
              }}>
                {mockup}
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

const fadeUp = {
  initial: { opacity: 0, y: 60 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

const stats = [
  { value: "5K+", label: "Bots Created" },
  { value: "500K+", label: "Conversations" },
  { value: "99.9%", label: "Uptime" },
  { value: "4.8★", label: "Rating" },
];

const testimonials = [
  { name: "Rahul Sharma", role: "CTO, FinEdge", initial: "R", color: BRAND.cyan, rating: 5, text: "Managing 15+ bots across our customer support teams was never this easy. The bot management hub is a game-changer for our ops team." },
  { name: "Ananya Gupta", role: "Product Lead, ShopLane", initial: "A", color: BRAND.purple, rating: 5, text: "The flow builder helped us design complex support flows in hours, not weeks. Our customer satisfaction score went up by 40% in 60 days." },
  { name: "Vikram Patel", role: "Founder, HealthFirst", initial: "V", color: BRAND.primary, rating: 5, text: "Domain whitelisting gave us the security we needed. Our patients love the instant responses, and our team loves the analytics." },
  { name: "Priya Mehta", role: "Head of Growth, ZoomMart", initial: "P", color: "#ec4899", rating: 5, text: "We went from 200 to 50,000 monthly conversations without scaling support headcount. ROI was visible in the first month of deployment." },
  { name: "Arjun Reddy", role: "VP Engineering, BuildPro", initial: "A", color: "#10b981", rating: 5, text: "The webhook and API integration is rock solid. We push conversation data to our CRM in real-time. Zero downtime in 8 months of production." },
  { name: "Sneha Iyer", role: "CMO, FoodHub", initial: "S", color: "#f59e0b", rating: 5, text: "Our lead conversion jumped 3.2x after deploying Talktokam on the landing pages. The AI suggestions are scarily accurate for our use case." },
  { name: "Karan Malhotra", role: "Founder, EduTech", initial: "K", color: "#8b5cf6", rating: 5, text: "Built a complete admissions counselor bot in 2 days. It handled 8,000+ student queries during our launch week without breaking a sweat." },
  { name: "Meera Joshi", role: "Director, TravelCo", initial: "M", color: "#06b6d4", rating: 5, text: "Multi-language support out of the box was a game-changer for our European expansion. Support quality remained top-tier across regions." },
];

const TestimonialCard = ({ t }) => (
  <Box sx={{
    position: "relative",
    p: 3,
    borderRadius: 3,
    background: "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    backdropFilter: "blur(20px)",
    willChange: "transform",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)",
    transition: "transform 0.3s ease, border-color 0.3s ease",
    "&:hover": { transform: "translateY(-4px)", borderColor: `${t.color}66` },
    height: "100%",
  }}>
    <Box sx={{ position: "absolute", top: 12, right: 16, opacity: 0.15 }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill={t.color}>
        <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
      </svg>
    </Box>
    <Box sx={{ display: "flex", gap: 0.3, mb: 1.5 }}>
      {Array.from({ length: t.rating }).map((_, i) => (
        <Box key={i} sx={{ color: "#fbbf24", fontSize: 14, textShadow: "0 0 6px rgba(251,191,36,0.4)" }}>★</Box>
      ))}
    </Box>
    <Typography sx={{ color: "#cbd5e1", fontSize: 13.5, lineHeight: 1.7, mb: 2.5, fontStyle: "italic", minHeight: 90 }}>
      "{t.text}"
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pt: 2, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      <Box sx={{ position: "relative", width: 42, height: 42, flexShrink: 0 }}>
        <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", background: `linear-gradient(135deg, ${t.color}, ${BRAND.cyan})`, padding: "2px" }}>
          <Box sx={{ width: "100%", height: "100%", borderRadius: "50%", background: "#0a0e27", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: t.color }}>{t.initial}</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</Typography>
        <Typography sx={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.role}</Typography>
      </Box>
    </Box>
  </Box>
);

const Home = () => {
  const navigate = useNavigate();
  const analyticsRef = useRef(null);
  const flowRef = useRef(null);
  const agentsRef = useRef(null);
  const chatRef = useRef(null);
  const { scrollYProgress: analyticsProgress } = useScroll({ target: analyticsRef, offset: ["start start", "end start"] });
  const { scrollYProgress: flowProgress } = useScroll({ target: flowRef, offset: ["start start", "end start"] });
  const { scrollYProgress: agentsProgress } = useScroll({ target: agentsRef, offset: ["start start", "end start"] });
  const { scrollYProgress: chatProgress } = useScroll({ target: chatRef, offset: ["start start", "end start"] });

  // Embed our own Talktokam chat widget on the public homepage only.
  // Set REACT_APP_SITE_BOT_ID to the published bot's id, and whitelist this
  // domain in Settings → Whitelist (otherwise the widget hides itself on a 403).
  useEffect(() => {
    const botId = process.env.REACT_APP_SITE_BOT_ID;
    if (!botId) {
      console.warn(
        "Talktokam: set REACT_APP_SITE_BOT_ID in chatbot-frontend/.env to show the chat widget on the homepage."
      );
      return;
    }
    const origin = window.location.origin;
    const api =
      process.env.REACT_APP_AUTH_API ||
      `${origin.replace(/:\d+$/, "")}:5000/api/auth`;
    window.TALKTOKAM_CONFIG = { id: botId, origin, api };

    const script = document.createElement("script");
    script.src = "/widget.js";
    script.async = true;
    script.id = "talktokam-widget-loader";
    document.body.appendChild(script);

    return () => {
      // Tear down the widget so it doesn't linger on the dashboard routes,
      // and clear the init flag so it re-loads when returning to the homepage.
      document.getElementById("talktokam-widget-loader")?.remove();
      document.getElementById("talktokam-root-" + botId)?.remove();
      document.getElementById("talktokam-panel-" + botId)?.remove();
      if (window.__TALKTOKAM_INITED) delete window.__TALKTOKAM_INITED[botId];
      delete window.TALKTOKAM_CONFIG;
    };
  }, []);

  const TypeWriter = ({ text, speed = 50 }) => {
    const [displayed, setDisplayed] = useState("");
    useEffect(() => {
      let i = 0;
      const timer = setInterval(() => {
        setDisplayed(text.slice(0, i + 1));
        i++;
        if (i >= text.length) clearInterval(timer);
      }, speed);
      return () => clearInterval(timer);
    }, [text, speed]);
    return <>{displayed}<span className="cursor-blink">|</span></>;
  };

  return (
    <Box sx={{ overflow: "hidden" }}>
      <Header />

      {/* ===== HERO ===== */}
      <Box sx={{ position: "relative", minHeight: "100vh", display: "flex", alignItems: "center", background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`, pt: { xs: 14, md: 12 }, pb: 8, overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`, backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)", pointerEvents: "none" }} />
        <motion.div
          animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "5%", left: "-5%", width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`, filter: "blur(60px)", pointerEvents: "none" }}
        />
        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", bottom: "-10%", right: "-10%", width: 600, height: 600, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`, filter: "blur(80px)", pointerEvents: "none" }}
        />
        <motion.div
          animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "50%", right: "30%", width: 300, height: 300, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`, filter: "blur(40px)", pointerEvents: "none" }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Box sx={{ display: "inline-flex", alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap", gap: 1, px: 2, py: 0.8, borderRadius: "30px", bgcolor: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.3)", mb: 3 }}>
                  <Box sx={{ position: "relative", display: "flex", flexShrink: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BRAND.cyan, boxShadow: `0 0 12px ${BRAND.cyan}` }} />
                    <Box sx={{ position: "absolute", width: 8, height: 8, borderRadius: "50%", bgcolor: BRAND.cyan, animation: "talktokamPulse 1.5s ease-out infinite" }} />
                  </Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND.cyan, textTransform: "uppercase", letterSpacing: "1.2px" }}>
                    AI-Powered Chatbot Platform
                  </Typography>
                  <Box sx={{ width: 1, height: 12, bgcolor: "rgba(56,189,248,0.3)", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>v2.0</Typography>
                </Box>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              >
                <Typography sx={{ fontSize: { xs: 44, md: 64, lg: 76 }, fontWeight: 800, lineHeight: 1, color: "#fff", mb: 3, letterSpacing: "-3px" }}>
                  Automate<br />
                  Conversations.<br />
                  <Box component="span" sx={{ background: `linear-gradient(135deg, ${BRAND.cyan} 0%, ${BRAND.purple} 50%, ${BRAND.primary} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundSize: "200% 200%", animation: "talktokamShimmer 4s ease-in-out infinite" }}>
                    Grow Faster.
                  </Box>
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                <Typography sx={{ fontSize: { xs: 16, md: 19 }, color: "#94a3b8", lineHeight: 1.7, mb: 4, maxWidth: 540, fontWeight: 400 }}>
                  <TypeWriter text="Build intelligent chatbots that engage visitors, qualify leads, and provide instant support — 24/7. No coding required." speed={25} />
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.35, ease: "easeOut" }}
              >
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate("/signup")}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      borderRadius: 2, textTransform: "none", fontWeight: 700, px: 4, py: 1.6, fontSize: 16,
                      background: `linear-gradient(135deg, ${BRAND.cyan} 0%, ${BRAND.purple} 100%)`,
                      boxShadow: `0 10px 40px ${BRAND.cyan}55, 0 0 0 1px rgba(255,255,255,0.1) inset`,
                      position: "relative", overflow: "hidden",
                      "&:hover": { boxShadow: `0 16px 50px ${BRAND.purple}77`, background: `linear-gradient(135deg, ${BRAND.cyanDark} 0%, ${BRAND.purpleDark} 100%)`, transform: "translateY(-2px)" },
                      transition: "all 0.3s ease",
                      "&::after": { content: '""', position: "absolute", top: 0, left: "-100%", width: "100%", height: "100%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)", transition: "left 0.6s", "&:hover": { left: "100%" } }
                    }}
                  >
                    Start Free Trial
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      borderRadius: 2, textTransform: "none", fontWeight: 600, px: 4, py: 1.6, fontSize: 16,
                      borderColor: "rgba(148,163,184,0.3)", color: "#e0e7ff", bgcolor: "rgba(255,255,255,0.03)",
                      "&:hover": { borderColor: BRAND.cyan, color: BRAND.cyan, bgcolor: "rgba(56,189,248,0.08)" }
                    }}
                  >
                    Watch Demo
                  </Button>
                </Box>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.6 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                  <Box sx={{ display: "flex" }}>
                    {[
                      { letter: "S", color: BRAND.cyan },
                      { letter: "J", color: BRAND.purple },
                      { letter: "M", color: BRAND.primary },
                      { letter: "L", color: "#ec4899" },
                    ].map((u, i) => (
                      <Avatar key={i} sx={{ width: 38, height: 38, border: `2px solid #0a0e27`, ml: i > 0 ? -1.2 : 0, bgcolor: u.color, fontSize: 13, fontWeight: 700, boxShadow: `0 0 12px ${u.color}66` }}>
                        {u.letter}
                      </Avatar>
                    ))}
                  </Box>
                  <Box>
                    <Box sx={{ display: "flex", gap: 0.3, mb: 0.3 }}>
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Box key={s} sx={{ color: "#fbbf24", fontSize: 14 }}>★</Box>
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                      <strong style={{ color: "#fff" }}>4K+</strong> businesses · <strong style={{ color: "#fff" }}>50M+</strong> messages
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.85, x: 40 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                style={{ perspective: "1500px" }}
              >
                <TiltCard max={8} scale={1.02}>
                  <Box sx={{ position: "relative" }}>
                    <Box sx={{ position: "absolute", top: -40, right: -20, width: 180, height: 80, borderRadius: 3, bgcolor: "rgba(10,14,39,0.8)", border: "1px solid rgba(56,189,248,0.3)", boxShadow: `0 10px 40px rgba(56,189,248,0.3)`, p: 2, zIndex: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 2, background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ color: "#fff", fontSize: 18, fontWeight: 800 }}>+24%</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Conversion</Typography>
                        <Typography sx={{ fontSize: 12, color: "#fff", fontWeight: 700 }}>This week</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ position: "absolute", bottom: -30, left: -20, width: 200, height: 70, borderRadius: 3, bgcolor: "rgba(10,14,39,0.8)", border: "1px solid rgba(168,85,247,0.3)", boxShadow: `0 10px 40px rgba(168,85,247,0.3)`, p: 1.5, zIndex: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: 1.5, background: `linear-gradient(135deg, ${BRAND.purple}, ${BRAND.primary})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <RocketLaunchIcon sx={{ color: "#fff", fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Active Bots</Typography>
                        <Typography sx={{ fontSize: 12, color: "#fff", fontWeight: 700 }}>12 running · 3 alerts</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ borderRadius: 4, overflow: "hidden", background: "linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)", backdropFilter: "blur(20px)", willChange: "transform", border: "1px solid rgba(255,255,255,0.1)", boxShadow: `0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05) inset, 0 0 100px ${BRAND.cyan}1a` }}>
                      <Box sx={{ bgcolor: "rgba(10,14,39,0.6)", px: 2.5, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ display: "flex", gap: 0.6 }}>
                          <Box sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: "#ef4444" }} />
                          <Box sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: "#f59e0b" }} />
                          <Box sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: "#10b981" }} />
                        </Box>
                        <Typography sx={{ fontSize: 11, color: "#64748b", ml: 1, fontWeight: 600 }}>talktokam.app/dashboard</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981", boxShadow: "0 0 8px #10b981" }} />
                        <Typography sx={{ fontSize: 10, color: "#10b981", fontWeight: 600 }}>Live</Typography>
                      </Box>
                      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                          {[
                            { name: "Support", chats: "1.2K", color: BRAND.cyan },
                            { name: "Sales", chats: "856", color: BRAND.purple },
                            { name: "FAQ", chats: "2.1K", color: BRAND.primary },
                          ].map((bot, i) => (
                            <motion.div
                              key={bot.name}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.7 + i * 0.1, duration: 0.4 }}
                              style={{ flex: 1 }}
                            >
                              <Box sx={{ bgcolor: "rgba(255,255,255,0.04)", borderRadius: 2, p: 1.5, border: `1px solid ${bot.color}33` }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.8 }}>
                                  <Box sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: `${bot.color}26`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <SmartToyIcon sx={{ color: bot.color, fontSize: 15 }} />
                                  </Box>
                                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#e0e7ff" }}>{bot.name}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: bot.color, lineHeight: 1 }}>{bot.chats}</Typography>
                                  <Typography sx={{ fontSize: 9, color: "#64748b" }}>chats</Typography>
                                </Box>
                              </Box>
                            </motion.div>
                          ))}
                        </Box>

                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.0, duration: 0.5 }}
                        >
                          <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: 2, p: 2, border: "1px solid rgba(255,255,255,0.06)" }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                                Live Conversation
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#10b981", boxShadow: "0 0 6px #10b981" }} />
                                <Typography sx={{ fontSize: 9, color: "#10b981", fontWeight: 700 }}>ACTIVE</Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-end" }}>
                              <Box sx={{ px: 1.5, py: 0.8, borderRadius: "10px 10px 10px 3px", bgcolor: "rgba(255,255,255,0.08)", color: "#e0e7ff", fontSize: 11, maxWidth: "75%" }}>
                                Hi, I need help with my order
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1, mb: 1, justifyContent: "flex-end" }}>
                              <Box sx={{ px: 1.5, py: 0.8, borderRadius: "10px 10px 3px 10px", background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, color: "#fff", fontSize: 11, maxWidth: "75%", fontWeight: 500 }}>
                                I'd love to help! Share your order ID?
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", gap: 0.5, mt: 1.5 }}>
                              <Box sx={{ flex: 1, px: 1.5, py: 0.7, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", fontSize: 10, color: "#64748b" }}>
                                Type a message…
                              </Box>
                              <Box sx={{ px: 2, py: 0.7, borderRadius: 1.5, background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center" }}>
                                <ArrowForwardIcon sx={{ fontSize: 12 }} />
                              </Box>
                            </Box>
                          </Box>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, scaleX: 0 }}
                          animate={{ opacity: 1, scaleX: 1 }}
                          transition={{ delay: 1.4, duration: 0.6 }}
                          style={{ transformOrigin: "left" }}
                        >
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Box sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: "rgba(56,189,248,0.1)", position: "relative", overflow: "hidden" }}>
                              <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "87%", borderRadius: 3, background: `linear-gradient(90deg, ${BRAND.cyan}, ${BRAND.purple})`, boxShadow: `0 0 12px ${BRAND.cyan}88` }} />
                            </Box>
                            <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>87% satisfaction</Typography>
                          </Box>
                        </motion.div>
                      </Box>
                    </Box>
                  </Box>
                </TiltCard>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== STICKY SECTION 1: ANALYTICS ===== */}
      <StickySection
        sectionRef={analyticsRef}
        progress={analyticsProgress}
        align="right"
        kicker="01 / Analytics"
        title="Track every"
        titleAccent="conversation."
        description="Real-time analytics dashboard with KPI cards, live status indicators, and a 7-day conversation chart — all updated the moment a chat ends."
        points={["5 live KPI cards: conversations, messages, active chats, top user, rating", "Live status bar with color-coded dots", "7-day bar chart with hover values", "Auto-refreshes every 30s"]}
        mockup={<MockAnalyticsDashboard />}
      />

      {/* ===== STICKY SECTION 2: FLOW BUILDER ===== */}
      <StickySection
        sectionRef={flowRef}
        progress={flowProgress}
        align="left"
        kicker="02 / Flow Builder"
        title="Design flows"
        titleAccent="visually."
        description="Drag-and-drop node editor with 19 question types. Connect decisions, validate answers, and preview the entire conversation in one canvas."
        points={["19 question types: text, choice, file upload, API, payment, and more", "Visual node graph with arrows & connections", "Live preview of every question & validation", "Drag from the left palette, click to edit on the right"]}
        mockup={<MockFlowBuilder />}
      />

      {/* ===== STICKY SECTION 3: AI AGENTS ===== */}
      <StickySection
        sectionRef={agentsRef}
        progress={agentsProgress}
        align="right"
        kicker="03 / AI Agents"
        title="AI personas for"
        titleAccent="every use case."
        description="Pre-built templates: Sales, Support, Lead Qualifier, Appointment, FAQ. Each agent has its own greeting, suggested questions, and conversation history."
        points={["6 pre-built templates ready to deploy", "Custom Q&A pairs with instant answers", "Per-agent greeting & avatar", "Track message count + toggle on/off"]}
        mockup={<MockAIAgentCard />}
      />

      {/* ===== STICKY SECTION 4: LIVE CHAT ===== */}
      <StickySection
        sectionRef={chatRef}
        progress={chatProgress}
        align="left"
        kicker="04 / Live Chat"
        title="Real conversations,"
        titleAccent="real-time."
        description="Watch your bot engage users in real-time. Quick reply chips, typing indicators, read receipts, and seamless handoff to human agents when needed."
        points={["Real-time message stream with timestamps & read receipts", "Quick reply chips for guided conversations", "Typing indicator for natural feel", "One-click handoff to human agent"]}
        mockup={<MockLiveChat />}
      />

      {/* ===== STATS ===== */}
      <Box sx={{ py: { xs: 8, md: 10 }, background: `linear-gradient(180deg, #0a0e27 0%, #1e1b4b 100%)`, position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.purple}1a 0%, transparent 70%)`, pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={4}>
            {stats.map((stat, i) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <motion.div
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5, ease: "easeOut" }}
                  style={{ textAlign: "center", transformStyle: "preserve-3d" }}
                >
                  <TiltCard max={8} scale={1.05}>
                    <Box sx={{
                      p: 3,
                      borderRadius: 3,
                      background: "linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      backdropFilter: "blur(20px)",
                      willChange: "transform",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
                    }}>
                      <Typography sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", lineHeight: 1, mb: 1 }}>
                        {stat.value}
                      </Typography>
                      <Typography sx={{ color: "#cbd5e1", fontWeight: 500, fontSize: 14 }}>{stat.label}</Typography>
                    </Box>
                  </TiltCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== FEATURES — Bento Grid ===== */}
      <Box id="features" sx={{ py: { xs: 10, md: 16 }, position: "relative", overflow: "hidden", background: "linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)" }}>
        <Box sx={{ position: "absolute", top: "5%", left: "5%", width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.cyan}1f 0%, transparent 70%)`, filter: "blur(80px)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "15%", right: "5%", width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.purple}1f 0%, transparent 70%)`, filter: "blur(80px)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(rgba(56,189,248,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.03) 1px, transparent 1px)`, backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div {...fadeUp}>
            <Box sx={{ textAlign: "center", mb: 10 }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 2.5, py: 0.8, borderRadius: "30px", bgcolor: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.3)", mb: 3 }}>
                <AutoGraphIcon sx={{ fontSize: 16, color: BRAND.cyan }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND.cyan, textTransform: "uppercase", letterSpacing: "1.5px" }}>
                  Powerful Features
                </Typography>
              </Box>
              <Typography sx={{ fontSize: { xs: 36, md: 54, lg: 64 }, fontWeight: 800, color: "#fff", mb: 2, letterSpacing: "-2.5px", lineHeight: 1.05 }}>
                Everything You Need to{" "}
                <Box component="span" sx={{ background: `linear-gradient(135deg, ${BRAND.cyan} 0%, ${BRAND.purple} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  Scale Conversations
                </Box>
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: 17, maxWidth: 640, mx: "auto", lineHeight: 1.7 }}>
                From bot creation to conversation analytics — a complete platform to build, deploy, and optimize your AI chatbots at scale.
              </Typography>
            </Box>
          </motion.div>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gridAutoRows: "minmax(200px, auto)", gap: 2.5 }}>
            <Box sx={{ gridColumn: { xs: "1", md: "span 2" }, gridRow: { xs: "auto", md: "span 2" } }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} style={{ height: "100%" }}>
                <TiltCard max={5} scale={1.01}>
                  <Box sx={{
                    height: "100%", position: "relative", p: { xs: 3, md: 4 }, borderRadius: 4, overflow: "hidden",
                    background: "linear-gradient(160deg, rgba(56,189,248,0.14) 0%, rgba(99,102,241,0.08) 50%, rgba(255,255,255,0.02) 100%)",
                    border: "1px solid rgba(56,189,248,0.25)", backdropFilter: "blur(20px)", willChange: "transform",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
                  }}>
                    <Box sx={{ position: "absolute", top: -60, right: -60, width: 220, height: 220, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.cyan}50 0%, transparent 70%)`, filter: "blur(50px)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", bottom: -40, left: -40, width: 180, height: 180, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.purple}30 0%, transparent 70%)`, filter: "blur(40px)", pointerEvents: "none" }} />
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, position: "relative" }}>
                      <Box sx={{ width: { xs: 56, md: 64 }, height: { xs: 56, md: 64 }, borderRadius: 2.5, background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.primary})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 10px 30px ${BRAND.cyan}66` }}>
                        <SmartToyIcon sx={{ fontSize: { xs: 28, md: 32 }, color: "#fff" }} />
                      </Box>
                      <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1, bgcolor: "rgba(56,189,248,0.15)", border: "1px solid rgba(56,189,248,0.3)" }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 800, color: BRAND.cyan, letterSpacing: "0.8px" }}>FEATURED</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: 22, md: 26 }, color: "#fff", mb: 1.5, letterSpacing: "-0.5px", position: "relative" }}>Bot Management Hub</Typography>
                    <Typography sx={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.7, mb: 3, position: "relative" }}>
                      Create, configure, and manage multiple chatbots from a single dashboard. Toggle active status, delete bots, and organize your AI workforce.
                    </Typography>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.05)", position: "relative" }}>
                      {[
                        { name: "Support Bot", status: "Active", statusColor: "#10b981", chats: "1.2K" },
                        { name: "Sales Bot", status: "Active", statusColor: "#10b981", chats: "856" },
                        { name: "FAQ Bot", status: "Paused", statusColor: "#f59e0b", chats: "2.1K" },
                      ].map((b, i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1, borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: b.statusColor, boxShadow: `0 0 8px ${b.statusColor}` }} />
                            <Typography sx={{ fontSize: 12, color: "#e0e7ff", fontWeight: 500 }}>{b.name}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{b.chats} chats</Typography>
                            <Typography sx={{ fontSize: 9, color: b.statusColor, fontWeight: 800, px: 1, py: 0.2, borderRadius: 0.8, bgcolor: `${b.statusColor}1a` }}>{b.status.toUpperCase()}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </TiltCard>
              </motion.div>
            </Box>

            <Box sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} style={{ height: "100%" }}>
                <TiltCard max={5} scale={1.01}>
                  <Box sx={{
                    height: "100%", position: "relative", p: 3.5, borderRadius: 4, overflow: "hidden",
                    background: "linear-gradient(160deg, rgba(168,85,247,0.14) 0%, rgba(99,102,241,0.08) 50%, rgba(255,255,255,0.02) 100%)",
                    border: "1px solid rgba(168,85,247,0.25)", backdropFilter: "blur(20px)", willChange: "transform",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
                  }}>
                    <Box sx={{ position: "absolute", top: -40, left: -40, width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.purple}50 0%, transparent 70%)`, filter: "blur(50px)", pointerEvents: "none" }} />
                    <Box sx={{ width: 56, height: 56, borderRadius: 2.5, background: `linear-gradient(135deg, ${BRAND.purple}, ${BRAND.primary})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 10px 30px ${BRAND.purple}66`, mb: 2.5, position: "relative" }}>
                      <AccountTreeIcon sx={{ fontSize: 28, color: "#fff" }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#fff", mb: 1, position: "relative" }}>Visual Flow Builder</Typography>
                    <Typography sx={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6, position: "relative" }}>
                      Design conversation flows with an intuitive builder. Map intents, responses, and decision trees — no code required.
                    </Typography>
                  </Box>
                </TiltCard>
              </motion.div>
            </Box>

            <Box sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }} style={{ height: "100%" }}>
                <TiltCard max={5} scale={1.01}>
                  <Box sx={{
                    height: "100%", position: "relative", p: 3.5, borderRadius: 4, overflow: "hidden",
                    background: "linear-gradient(160deg, rgba(99,102,241,0.14) 0%, rgba(56,189,248,0.08) 50%, rgba(255,255,255,0.02) 100%)",
                    border: "1px solid rgba(99,102,241,0.25)", backdropFilter: "blur(20px)", willChange: "transform",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
                  }}>
                    <Box sx={{ position: "absolute", top: -30, right: -30, width: 180, height: 180, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.primary}40 0%, transparent 70%)`, filter: "blur(50px)", pointerEvents: "none" }} />
                    <Box sx={{ width: 56, height: 56, borderRadius: 2.5, background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.cyan})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 10px 30px ${BRAND.primary}66`, mb: 2.5, position: "relative" }}>
                      <BarChartIcon sx={{ fontSize: 28, color: "#fff" }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#fff", mb: 1, position: "relative" }}>Conversation Analytics</Typography>
                    <Typography sx={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6, position: "relative" }}>
                      Track every interaction with rich analytics. Monitor chats, sentiment, and trends in real-time.
                    </Typography>
                  </Box>
                </TiltCard>
              </motion.div>
            </Box>

            <Box sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.25 }} style={{ height: "100%" }}>
                <TiltCard max={5} scale={1.01}>
                  <Box sx={{
                    height: "100%", position: "relative", p: 3, borderRadius: 4, overflow: "hidden",
                    background: "linear-gradient(160deg, rgba(236,72,153,0.14) 0%, rgba(168,85,247,0.08) 50%, rgba(255,255,255,0.02) 100%)",
                    border: "1px solid rgba(236,72,153,0.25)", backdropFilter: "blur(20px)", willChange: "transform",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
                    display: "flex", alignItems: "center", gap: 2.5,
                  }}>
                    <Box sx={{ position: "absolute", top: -30, right: -30, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(236,72,153,0.4) 0%, transparent 70%)", filter: "blur(50px)", pointerEvents: "none" }} />
                    <Box sx={{ width: 52, height: 52, borderRadius: 2.5, flexShrink: 0, background: "linear-gradient(135deg, #ec4899, #a855f7)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 30px rgba(236,72,153,0.66)", position: "relative" }}>
                      <ChatIcon sx={{ fontSize: 26, color: "#fff" }} />
                    </Box>
                    <Box sx={{ position: "relative" }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#fff", mb: 0.5 }}>Live Chat Handoff</Typography>
                      <Typography sx={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.55 }}>
                        Escalate from bot to human seamlessly. Set agent availability and never miss a customer query.
                      </Typography>
                    </Box>
                  </Box>
                </TiltCard>
              </motion.div>
            </Box>

            <Box sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} style={{ height: "100%" }}>
                <TiltCard max={5} scale={1.01}>
                  <Box sx={{
                    height: "100%", position: "relative", p: 3, borderRadius: 4, overflow: "hidden",
                    background: "linear-gradient(160deg, rgba(16,185,129,0.14) 0%, rgba(56,189,248,0.08) 50%, rgba(255,255,255,0.02) 100%)",
                    border: "1px solid rgba(16,185,129,0.25)", backdropFilter: "blur(20px)", willChange: "transform",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
                    display: "flex", alignItems: "center", gap: 2.5,
                  }}>
                    <Box sx={{ position: "absolute", top: -30, right: -30, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(16,185,129,0.4) 0%, transparent 70%)", filter: "blur(50px)", pointerEvents: "none" }} />
                    <Box sx={{ width: 52, height: 52, borderRadius: 2.5, flexShrink: 0, background: "linear-gradient(135deg, #10b981, #06b6d4)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 30px rgba(16,185,129,0.66)", position: "relative" }}>
                      <SecurityIcon sx={{ fontSize: 26, color: "#fff" }} />
                    </Box>
                    <Box sx={{ position: "relative" }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#fff", mb: 0.5 }}>Domain Whitelist</Typography>
                      <Typography sx={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.55 }}>
                        Control exactly where your bots can be embedded with per-bot domain whitelisting.
                      </Typography>
                    </Box>
                  </Box>
                </TiltCard>
              </motion.div>
            </Box>

            <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.35 }} style={{ height: "100%" }}>
                <TiltCard max={5} scale={1.01}>
                  <Box sx={{
                    height: "100%", position: "relative", p: { xs: 3, md: 3.5 }, borderRadius: 4, overflow: "hidden",
                    background: "linear-gradient(135deg, rgba(56,189,248,0.14) 0%, rgba(168,85,247,0.14) 50%, rgba(99,102,241,0.14) 100%)",
                    border: "1px solid rgba(99,102,241,0.3)", backdropFilter: "blur(20px)", willChange: "transform",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
                    display: "flex", alignItems: "center", gap: { xs: 2, md: 3 }, flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}>
                    <Box sx={{ position: "absolute", top: -50, right: -50, width: 250, height: 250, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.purple}30 0%, transparent 70%)`, filter: "blur(60px)", pointerEvents: "none" }} />
                    <Box sx={{ width: { xs: 60, md: 72 }, height: { xs: 60, md: 72 }, borderRadius: 3, flexShrink: 0, background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 12px 40px ${BRAND.purple}66`, position: "relative" }}>
                      <StorageIcon sx={{ fontSize: { xs: 30, md: 36 }, color: "#fff" }} />
                    </Box>
                    <Box sx={{ flex: 1, position: "relative" }}>
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, md: 24 }, color: "#fff", mb: 0.8, letterSpacing: "-0.5px" }}>Conversation History</Typography>
                      <Typography sx={{ color: "#94a3b8", fontSize: { xs: 13, md: 14 }, lineHeight: 1.6, maxWidth: 720 }}>
                        Browse, search, and review every past conversation. Filter by status, dive into individual sessions, and export insights to retrain your models.
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, position: "relative", flexShrink: 0 }}>
                      {[{ v: "10K+", l: "Logs/day" }, { v: "<100ms", l: "Search" }, { v: "CSV/PDF", l: "Export" }].map((m, i) => (
                        <Box key={i} sx={{ px: 2, py: 1.2, borderRadius: 2, bgcolor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)", textAlign: "center", minWidth: 80 }}>
                          <Typography sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 800, background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", lineHeight: 1 }}>{m.v}</Typography>
                          <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, mt: 0.3, textTransform: "uppercase", letterSpacing: "0.5px" }}>{m.l}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </TiltCard>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ===== TESTIMONIALS — Marquee Carousel ===== */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: "relative", overflow: "hidden", background: "linear-gradient(180deg, #0a0e27 0%, #050816 100%)" }}>
        <Box sx={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", width: 700, height: 400, borderRadius: "50%", background: `radial-gradient(ellipse, ${BRAND.purple}26 0%, transparent 70%)`, filter: "blur(60px)", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, mb: 8 }}>
          <motion.div {...fadeUp}>
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 2.5, py: 0.8, borderRadius: "30px", bgcolor: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.3)", mb: 3 }}>
                <GradeIcon sx={{ fontSize: 16, color: "#fbbf24" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND.purple, textTransform: "uppercase", letterSpacing: "1.5px" }}>
                  Loved by Thousands
                </Typography>
              </Box>
              <Typography sx={{ fontSize: { xs: 36, md: 54, lg: 60 }, fontWeight: 800, color: "#fff", mb: 2, letterSpacing: "-2.5px", lineHeight: 1.05 }}>
                What our{" "}
                <Box component="span" sx={{ background: `linear-gradient(135deg, ${BRAND.cyan} 0%, ${BRAND.purple} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  customers
                </Box>
                {" "}say
              </Typography>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 2, mt: 1 }}>
                <Box sx={{ display: "flex", gap: 0.3 }}>
                  {[1, 2, 3, 4, 5].map((s) => <Box key={s} sx={{ color: "#fbbf24", fontSize: 20, textShadow: "0 0 8px rgba(251,191,36,0.5)" }}>★</Box>)}
                </Box>
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>4.8</Typography>
                <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>· from 2,400+ reviews on G2 & Capterra</Typography>
              </Box>
            </Box>
          </motion.div>
        </Container>

        <Box sx={{ position: "relative", "&::before, &::after": { content: '""', position: "absolute", top: 0, bottom: 0, width: 120, zIndex: 2, pointerEvents: "none" }, "&::before": { left: 0, background: "linear-gradient(90deg, #050816 0%, transparent 100%)" }, "&::after": { right: 0, background: "linear-gradient(270deg, #050816 0%, transparent 100%)" } }}>
          <Box className="marquee-track" sx={{ mb: 2.5 }}>
            {[...testimonials.slice(0, 4), ...testimonials.slice(0, 4)].map((t, i) => (
              <Box key={`r1-${i}`} sx={{ width: 380, flexShrink: 0 }}>
                <TestimonialCard t={t} />
              </Box>
            ))}
          </Box>
          <Box className="marquee-track reverse">
            {[...testimonials.slice(4, 8), ...testimonials.slice(4, 8)].map((t, i) => (
              <Box key={`r2-${i}`} sx={{ width: 380, flexShrink: 0 }}>
                <TestimonialCard t={t} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ===== CTA ===== */}
      <Box id="contact" sx={{ py: { xs: 10, md: 14 }, background: `linear-gradient(135deg, #0a0e27 0%, #1e1b4b 50%, #0a0e27 100%)`, position: "relative", overflow: "hidden" }}>
        <motion.div
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "10%", left: "8%", width: 100, height: 100, borderRadius: "20px", background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, opacity: 0.3, pointerEvents: "none" }}
        />
        <motion.div
          animate={{ y: [0, 20, 0], rotate: [0, -8, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", bottom: "15%", right: "10%", width: 140, height: 140, borderRadius: "50%", background: `linear-gradient(135deg, ${BRAND.purple}, ${BRAND.cyan})`, opacity: 0.25, pointerEvents: "none" }}
        />
        <motion.div
          animate={{ y: [0, -15, 0], x: [0, 10, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "50%", right: "20%", width: 60, height: 60, borderRadius: "12px", background: BRAND.cyan, opacity: 0.2, pointerEvents: "none" }}
        />
        <motion.div
          animate={{ y: [0, 15, 0], x: [0, -10, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", bottom: "25%", left: "20%", width: 80, height: 80, borderRadius: "50%", background: BRAND.purple, opacity: 0.2, pointerEvents: "none" }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotateX: -10 }}
            whileInView={{ opacity: 1, scale: 1, rotateX: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            style={{ perspective: "1200px" }}
          >
            <TiltCard max={6} scale={1.01}>
              <Box sx={{
                textAlign: "center",
                background: "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                backdropFilter: "blur(20px)", willChange: "transform",
                borderRadius: 4,
                p: { xs: 4, md: 7 },
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)"
              }}>
                <Box component="img" src={logoImg} alt="Talktokam" sx={{ height: 64, width: "auto", borderRadius: "12px", mx: "auto", mb: 3, display: "block", boxShadow: "0 12px 40px rgba(99,102,241,0.4)" }} />
                <Typography sx={{ fontSize: { xs: 32, md: 44 }, fontWeight: 800, color: "#fff", mb: 2, letterSpacing: "-1px" }}>
                  Ready to Build Your{" "}
                  <Box component="span" sx={{ background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    First Bot?
                  </Box>
                </Typography>
                <Typography sx={{ color: "#cbd5e1", fontSize: 17, mb: 4, maxWidth: 520, mx: "auto", lineHeight: 1.7 }}>
                  Join thousands of businesses using Talktokam to automate conversations and delight customers — no coding required.
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    placeholder="Enter your work email"
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "rgba(255,255,255,0.08)", minWidth: 280, "& fieldset": { borderColor: "rgba(255,255,255,0.15)" }, "&:hover fieldset": { borderColor: `${BRAND.cyan}66` }, "&.Mui-focused fieldset": { borderColor: BRAND.cyan } }, "& input": { color: "#fff" } }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => navigate("/signup")}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      borderRadius: 2, textTransform: "none", fontWeight: 700, px: 4, py: 1.5, fontSize: 15,
                      background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`,
                      boxShadow: `0 10px 30px ${BRAND.cyan}55`,
                      "&:hover": { boxShadow: `0 14px 40px ${BRAND.purple}77`, background: `linear-gradient(135deg, ${BRAND.cyanDark}, ${BRAND.purpleDark})` }
                    }}
                  >
                    Get Started Free
                  </Button>
                </Box>
                <Typography sx={{ color: "#94a3b8", fontSize: 12, mt: 3 }}>No credit card required · Free 14-day trial</Typography>
              </Box>
            </TiltCard>
          </motion.div>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default Home;